"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, FileCheck2, Mail, Phone, Send, Loader2, RefreshCw } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  help?: string;
  required?: boolean;
  options?: Array<{
    value: string;
    label: string;
    price?: number;
  }>;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id: number;
  key: string;
  name: string;
  description: string;
  availability?: {
    accepting: boolean;
    state: string;
    message: string;
  };
  fields: FormFieldDefinition[];
  submit?: {
    label: string;
    processing_label?: string;
  };
  spam?: {
    token: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
    challenge?: any;
  };
}
const FORM_MESSAGES = {
  serviceUnavailable: {
    en: "Form service unavailable. Please refresh or contact engineering directly via email.",
    zh: "表单服务尚未就绪，请刷新重试或直接通过电子邮箱与我们联系。",
    es: "Servicio de formulario no disponible. Actualice la página o contacte a ingeniería por correo electrónico.",
    de: "Formulardienst nicht verfügbar. Bitte aktualisieren Sie die Seite oder kontaktieren Sie die Technik per E-Mail.",
    fr: "Service de formulaire indisponible. Veuillez actualiser ou contacter le service technique par e-mail.",
    pt: "Serviço de formulário indisponível. Atualize a página ou entre em contato com a engenharia por e-mail."
  },
  rateLimited: {
    en: "Please wait a moment before trying again.",
    zh: "请求过于频繁，请稍候再试。",
    es: "Espere un momento antes de volver a intentarlo.",
    de: "Bitte warten Sie einen Moment, bevor Sie es erneut versuchen.",
    fr: "Veuillez patienter un instant avant de réessayer.",
    pt: "Aguarde um momento antes de tentar novamente."
  },
  loadError: {
    en: "Unable to load inquiry form. Please refresh or contact technical sales directly.",
    zh: "无法加载询盘表单，请稍后重试或直接联系技术销售邮箱。",
    es: "No se puede cargar el formulario de consulta. Actualice o contacte directamente a ventas técnicas.",
    de: "Anfrageformular konnte nicht geladen werden. Bitte aktualisieren Sie oder kontaktieren Sie den technischen Vertrieb direkt.",
    fr: "Impossible de charger le formulaire de demande. Veuillez actualiser ou contacter directement le service technico-commercial.",
    pt: "Não foi possível carregar o formulário de consulta. Atualize ou entre em contato diretamente com as vendas técnicas."
  },
  loadErrorTitle: {
    en: "Unable to load inquiry form",
    zh: "无法加载询盘表单",
    es: "No se puede cargar el formulario de consulta",
    de: "Anfrageformular kann nicht geladen werden",
    fr: "Impossible de charger le formulaire de demande",
    pt: "Não foi possível carregar o formulário de consulta"
  },
  loadErrorDescription: {
    en: "Failed to load form definition. Please retry.",
    zh: "表单加载失败，请检查网络连接或稍后重试。",
    es: "Error al cargar la definición del formulario. Vuelva a intentarlo.",
    de: "Formulardefinition konnte nicht geladen werden. Bitte erneut versuchen.",
    fr: "Échec du chargement de la configuration du formulaire. Veuillez réessayer.",
    pt: "Falha ao carregar a definição do formulário. Tente novamente."
  },
  retryLoading: {
    en: "Retry loading form",
    zh: "重试加载表单",
    es: "Reintentar carga del formulario",
    de: "Formular neu laden",
    fr: "Réessayer le chargement du formulaire",
    pt: "Tentar carregar o formulário novamente"
  },
  submitSuccessTitle: {
    en: "Inquiry Transmitted",
    zh: "询盘已成功发送",
    es: "Consulta enviada con éxito",
    de: "Anfrage erfolgreich übermittelt",
    fr: "Demande transmise avec succès",
    pt: "Consulta enviada com sucesso"
  },
  submitSuccessDefaultHtml: {
    en: "<p>Thank you for contacting MutualFilter. Our technical engineering and sales team will review your inquiry.</p>",
    zh: "<p>感谢您联系 MutualFilter。我们的技术工程与销售团队将评估您的需求并与您联系。</p>",
    es: "<p>Gracias por contactar a MutualFilter. Nuestro equipo de ventas e ingeniería técnica evaluará su consulta.</p>",
    de: "<p>Vielen Dank für Ihre Kontaktaufnahme mit MutualFilter. Unser technisches Vertriebs- und Ingenieurteam wird Ihre Anfrage prüfen.</p>",
    fr: "<p>Merci d'avoir contacté MutualFilter. Notre équipe technique et commerciale examinera votre demande.</p>",
    pt: "<p>Obrigado por entrar em contato com a MutualFilter. Nossa equipe técnica de engenharia e vendas analisará sua consulta.</p>"
  },
  submitAnother: {
    en: "Submit Another Inquiry",
    zh: "提交新的询盘",
    es: "Enviar otra consulta",
    de: "Weitere Anfrage senden",
    fr: "Envoyer une autre demande",
    pt: "Enviar outra consulta"
  },
  noteTitle: {
    en: "Note",
    zh: "提示",
    es: "Nota",
    de: "Hinweis",
    fr: "Remarque",
    pt: "Nota"
  },
  networkError: {
    en: "A network or server error occurred while sending your inquiry. Please try again.",
    zh: "发送请求时发生网络或服务器错误，请稍后重试。",
    es: "Se produjo un error de red o servidor al enviar la consulta. Inténtelo de nuevo.",
    de: "Beim Senden Ihrer Anfrage ist ein Netzwerk- oder Serverfehler aufgetreten. Bitte versuchen Sie es erneut.",
    fr: "Une erreur réseau ou serveur est survenue lors de l'envoi de votre demande. Veuillez réessayer.",
    pt: "Ocorreu um erro de rede ou servidor ao enviar sua consulta. Tente novamente."
  },
  notAccepted: {
    en: "Submission was not accepted. Please review and try again.",
    zh: "提交未能成功接收，请核对信息后重试。",
    es: "El envío no fue aceptado. Revise los datos e inténtelo de nuevo.",
    de: "Die Übermittlung wurde nicht akzeptiert. Bitte überprüfen und erneut versuchen.",
    fr: "L'envoi n'a pas été accepté. Veuillez vérifier et réessayer.",
    pt: "O envio não foi aceito. Verifique os dados e tente novamente."
  },
  checkHighlighted: {
    en: "Please check the highlighted fields.",
    zh: "请核对标红的输入项。",
    es: "Compruebe los campos resaltados.",
    de: "Bitte überprüfen Sie die markierten Felder.",
    fr: "Veuillez vérifier les champs surlignés.",
    pt: "Verifique os campos destacados."
  },
  tokenExpired: {
    en: "Security token expired and could not be refreshed. Please reload the page.",
    zh: "安全凭证已过期，刷新失败，请重新加载页面重试。",
    es: "El token de seguridad ha caducado y no se pudo actualizar. Recargue la página.",
    de: "Das Sicherheitstoken ist abgelaufen und konnte nicht erneuert werden. Bitte laden Sie die Seite neu.",
    fr: "Le jeton de sécurité a expiré et n'a pas pu être actualisé. Veuillez recharger la page.",
    pt: "O token de segurança expirou e não pôde ser atualizado. Recarregue a página."
  },
  nameRequired: {
    en: "Full name is required",
    zh: "请输入姓名",
    es: "El nombre completo es obligatorio",
    de: "Vollständiger Name ist erforderlich",
    fr: "Le nom complet est requis",
    pt: "O nome completo é obrigatório"
  },
  nameMinLength: {
    en: "Please enter at least 2 characters",
    zh: "请至少输入 2 个字符",
    es: "Introduzca al menos 2 caracteres",
    de: "Bitte mindestens 2 Zeichen eingeben",
    fr: "Veuillez saisir au moins 2 caractères",
    pt: "Insira pelo menos 2 caracteres"
  },
  companyRequired: {
    en: "Company name is required",
    zh: "请输入公司名称",
    es: "El nombre de la empresa es obligatorio",
    de: "Unternehmensname ist erforderlich",
    fr: "Le nom de l'entreprise est requis",
    pt: "O nome da empresa é obrigatório"
  },
  emailRequired: {
    en: "Business email is required",
    zh: "请输入企业邮箱",
    es: "El correo corporativo es obligatorio",
    de: "Geschäftliche E-Mail ist erforderlich",
    fr: "L'e-mail professionnel est requis",
    pt: "O e-mail corporativo é obrigatório"
  },
  emailInvalid: {
    en: "Please enter a valid email address",
    zh: "请输入有效的邮箱地址",
    es: "Introduzca una dirección de correo válida",
    de: "Bitte eine gültige E-Mail-Adresse eingeben",
    fr: "Veuillez saisir une adresse e-mail valide",
    pt: "Insira um endereço de e-mail válido"
  },
  messageRequired: {
    en: "Please provide details about your inquiry",
    zh: "请填写您的具体需求与技术参数",
    es: "Indique los detalles de su consulta",
    de: "Bitte geben Sie Details zu Ihrer Anfrage an",
    fr: "Veuillez préciser les détails de votre demande",
    pt: "Forneça detalhes sobre sua consulta"
  },
  messageMinLength: {
    en: "Please provide at least 10 characters",
    zh: "请至少输入 10 个字符",
    es: "Introduzca al menos 10 caracteres",
    de: "Bitte mindestens 10 Zeichen eingeben",
    fr: "Veuillez saisir au moins 10 caractères",
    pt: "Insira pelo menos 10 caracteres"
  },
  labelFullName: {
    en: "Full Name *",
    zh: "姓名 / 负责人 *",
    es: "Nombre completo *",
    de: "Vollständiger Name *",
    fr: "Nom complet *",
    pt: "Nome completo *"
  },
  placeholderFullName: {
    en: "e.g. John Doe",
    zh: "例如：张经理 / John Doe",
    es: "p. ej. Carlos García",
    de: "z. B. Thomas Müller",
    fr: "ex. Jean Dupont",
    pt: "ex. Carlos Silva"
  },
  labelCompany: {
    en: "Company Name *",
    zh: "公司名称 *",
    es: "Nombre de la empresa *",
    de: "Unternehmensname *",
    fr: "Nom de l'entreprise *",
    pt: "Nome da empresa *"
  },
  placeholderCompany: {
    en: "e.g. Acme Filtration Corp",
    zh: "例如：某某过滤设备制造有限公司",
    es: "p. ej. Filtraciones Industriales S.L.",
    de: "z. B. Industrielle Filtration GmbH",
    fr: "ex. Filtration Industrielle SAS",
    pt: "ex. Filtração Industrial Ltda"
  },
  labelEmail: {
    en: "Business Email *",
    zh: "企业工作邮箱 *",
    es: "Correo corporativo *",
    de: "Geschäftliche E-Mail *",
    fr: "E-mail professionnel *",
    pt: "E-mail corporativo *"
  },
  labelPhone: {
    en: "Phone Number",
    zh: "联系电话",
    es: "Teléfono de contacto",
    de: "Telefonnummer",
    fr: "Numéro de téléphone",
    pt: "Telefone de contato"
  },
  placeholderPhone: {
    en: "+1 (555) 000-0000",
    zh: "+86 138-0000-0000 / +1 (555) 000-0000",
    es: "+34 912 345 678",
    de: "+49 30 12345678",
    fr: "+33 1 23 45 67 89",
    pt: "+55 11 91234-5678"
  },
  labelMessage: {
    en: "Inquiry & Technical Requirements *",
    zh: "询价需求与技术参数 *",
    es: "Consulta y requisitos técnicos *",
    de: "Anfrage & technische Anforderungen *",
    fr: "Demande & spécifications techniques *",
    pt: "Consulta e requisitos técnicos *"
  },
  placeholderMessage: {
    en: "Specify mesh grade (304, 316, 316L), mesh count, aperture size, roll dimensions, or specific industrial application...",
    zh: "请详细说明材质牌号 (304, 316, 316L)、目数、孔径尺寸、卷料规格或具体工业应用环境...",
    es: "Especifique el grado de malla (304, 316, 316L), número de malla, tamaño de abertura, dimensiones del rollo o aplicación industrial...",
    de: "Geben Sie Werkstoff (304, 316, 316L), Maschenzahl, Maschenweite, Rollenabmessungen oder die industrielle Anwendung an...",
    fr: "Précisez la nuance de toile (304, 316, 316L), l'ouverture de maille, les dimensions des rouleaux ou l'application industrielle...",
    pt: "Especifique o grau da malha (304, 316, 316L), contagem de malha, abertura, dimensões do rolo ou aplicação industrial..."
  },
  submittingText: {
    en: "Processing Inquiry...",
    zh: "正在提交询盘...",
    es: "Procesando consulta...",
    de: "Anfrage wird übermittelt...",
    fr: "Traitement de la demande en cours...",
    pt: "Processando consulta..."
  },
  submitButtonText: {
    en: "Submit General Inquiry",
    zh: "提交通用咨询",
    es: "Enviar consulta general",
    de: "Allgemeine Anfrage senden",
    fr: "Envoyer la demande générale",
    pt: "Enviar consulta geral"
  },
  privacyNotice: {
    en: "We respect your privacy. Technical drawings and specs remain strictly confidential.",
    zh: "我们高度重视客户隐私。所有技术图纸及规格参数均严格保密。",
    es: "Respetamos su privacidad. Los planos técnicos y especificaciones se tratan con estricta confidencialidad.",
    de: "Wir respektieren Ihre Privatsphäre. Technische Zeichnungen und Spezifikationen werden streng vertraulich behandelt.",
    fr: "Nous respectons votre confidentialité. Les plans techniques et spécifications restent strictement confidentiels.",
    pt: "Respeitamos a sua privacidade. Desenhos técnicos e especificações permanecem estritamente confidenciais."
  }
};
function ContactInquiryForm({
  lang
}: {
  lang: Lang;
}) {
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [loadErrorMsg, setLoadErrorMsg] = useState<string>("");
  const [formData, setFormData] = useState({
    full_name: "",
    company: "",
    email: "",
    phone: "",
    message: ""
  });
  const [honeypotVal, setHoneypotVal] = useState("");
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);
  const [confirmationHtml, setConfirmationHtml] = useState<string>("");
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const fetchFormDefinition = useCallback(async (): Promise<FormDefinition | null> => {
    setLoadState("loading");
    setLoadErrorMsg("");
    setGeneralError(null);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      if (isMountedRef.current) {
        setLoadErrorMsg(pick(lang, FORM_MESSAGES.serviceUnavailable));
        setLoadState("error");
      }
      return null;
    }
    try {
      const def: FormDefinition = await client.pluginRequest("wvc-forms/v1/forms/contact-inquiry");
      if (isMountedRef.current) {
        setFormDef(def);
        setFetchTimestamp(Date.now());
        setLoadState("ready");
      }
      return def;
    } catch (err: any) {
      if (err?.status === 404) {
        try {
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const items: Array<{
            key: string;
            name?: string;
            description?: string;
          }> = listRes?.items || [];
          let matched = items.find(item => item.key === "contact-inquiry");
          if (!matched && items.length === 1) {
            matched = items[0];
          }
          if (!matched) {
            matched = items.find(item => item.name && (item.name.toLowerCase().includes("inquiry") || item.name.toLowerCase().includes("contact")));
          }
          if (matched?.key) {
            const resolved: FormDefinition = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
            if (isMountedRef.current) {
              setFormDef(resolved);
              setFetchTimestamp(Date.now());
              setLoadState("ready");
            }
            return resolved;
          }
        } catch (_) {
          // fall through to error handling
        }
      }
      if (isMountedRef.current) {
        const errorText = err?.status === 429 ? pick(lang, FORM_MESSAGES.rateLimited) : pick(lang, FORM_MESSAGES.loadError);
        setLoadErrorMsg(errorText);
        setLoadState("error");
      }
      return null;
    }
  }, [lang]);
  useEffect(() => {
    fetchFormDefinition();
  }, [fetchFormDefinition]);
  const handleInputChange = (fieldKey: "full_name" | "company" | "email" | "phone" | "message", val: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: val
    }));
    const fDef = formDef?.fields?.find(f => f.key === fieldKey);
    const errKey = fDef?.id || fieldKey;
    if (fieldErrors[errKey]) {
      setFieldErrors(prev => {
        const copy = {
          ...prev
        };
        delete copy[errKey];
        return copy;
      });
    }
    if (generalError) {
      setGeneralError(null);
    }
  };
  const getFieldDef = (key: string): FormFieldDefinition | undefined => {
    return formDef?.fields?.find(f => f.key === key);
  };
  const handleReset = () => {
    setFormData({
      full_name: "",
      company: "",
      email: "",
      phone: "",
      message: ""
    });
    setHoneypotVal("");
    setFieldErrors({});
    setGeneralError(null);
    setSubmittedSuccess(false);
    setConfirmationHtml("");
    fetchFormDefinition();
  };
  const performSubmit = async (targetForm: FormDefinition, currentValues: Record<string, string>, honeypotAnswer: string, retryCount: number = 0): Promise<void> => {
    const minSec = targetForm.spam?.min_seconds ?? 3;
    const elapsedSec = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSec < minSec) {
      const waitMs = Math.ceil((minSec - elapsedSec + 0.1) * 1000);
      await new Promise(resolve => setTimeout(resolve, waitMs));
    }
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setGeneralError(pick(lang, FORM_MESSAGES.serviceUnavailable));
      setSubmitting(false);
      return;
    }
    const payload: {
      values: Record<string, string>;
      token?: string;
      hp?: Record<string, string>;
      meta: {
        source_url: string;
        referrer: string;
      };
    } = {
      values: currentValues,
      token: targetForm.spam?.token,
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    if (targetForm.spam?.honeypot?.field) {
      payload.hp = {
        [targetForm.spam.honeypot.field]: honeypotAnswer
      };
    }
    try {
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${targetForm.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res && res.success === true) {
        if (res.confirmation?.type === "redirect" && res.confirmation.url) {
          window.location.assign(res.confirmation.url);
          return;
        }
        if (res.confirmation?.message) {
          setConfirmationHtml(res.confirmation.message);
        } else {
          setConfirmationHtml(pick(lang, FORM_MESSAGES.submitSuccessDefaultHtml));
        }
        setSubmittedSuccess(true);
        setSubmitting(false);
      } else if (res && res.code === "frms_invalid_token" && retryCount === 0) {
        const freshDef = await fetchFormDefinition();
        if (freshDef) {
          const freshMinSec = freshDef.spam?.min_seconds ?? 3;
          await new Promise(resolve => setTimeout(resolve, freshMinSec * 1000));
          return performSubmit(freshDef, currentValues, honeypotAnswer, 1);
        } else {
          setGeneralError(pick(lang, FORM_MESSAGES.tokenExpired));
          setSubmitting(false);
        }
      } else if (res && res.code === "frms_validation_failed") {
        setFieldErrors(res.errors ?? {});
        setGeneralError(res.message || pick(lang, FORM_MESSAGES.checkHighlighted));
        setSubmitting(false);
      } else {
        setGeneralError(res?.message || pick(lang, FORM_MESSAGES.notAccepted));
        setSubmitting(false);
      }
    } catch (err: any) {
      if (err?.status === 429 || err?.code === "frms_rate_limited") {
        setGeneralError(pick(lang, FORM_MESSAGES.rateLimited));
      } else {
        setGeneralError(err?.message || pick(lang, FORM_MESSAGES.networkError));
      }
      setSubmitting(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || submitting) return;
    setGeneralError(null);
    setFieldErrors({});
    const fullNameDef = getFieldDef("full_name");
    const companyDef = getFieldDef("company");
    const emailDef = getFieldDef("email");
    const phoneDef = getFieldDef("phone");
    const messageDef = getFieldDef("message");
    const clientErrors: Record<string, {
      message: string;
    }> = {};

    // Validate full name
    const nameTrimmed = formData.full_name.trim();
    if (!nameTrimmed) {
      const errKey = fullNameDef?.id || "full_name";
      clientErrors[errKey] = {
        message: lang === "en" && fullNameDef?.validation?.messages?.required ? fullNameDef.validation.messages.required : pick(lang, FORM_MESSAGES.nameRequired)
      };
    } else if (nameTrimmed.length < 2) {
      const errKey = fullNameDef?.id || "full_name";
      clientErrors[errKey] = {
        message: pick(lang, FORM_MESSAGES.nameMinLength)
      };
    }

    // Validate company name
    const companyTrimmed = formData.company.trim();
    if (!companyTrimmed) {
      const errKey = companyDef?.id || "company";
      clientErrors[errKey] = {
        message: lang === "en" && companyDef?.validation?.messages?.required ? companyDef.validation.messages.required : pick(lang, FORM_MESSAGES.companyRequired)
      };
    }

    // Validate email
    const emailTrimmed = formData.email.trim();
    if (!emailTrimmed) {
      const errKey = emailDef?.id || "email";
      clientErrors[errKey] = {
        message: lang === "en" && emailDef?.validation?.messages?.required ? emailDef.validation.messages.required : pick(lang, FORM_MESSAGES.emailRequired)
      };
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      const errKey = emailDef?.id || "email";
      clientErrors[errKey] = {
        message: lang === "en" && emailDef?.validation?.messages?.invalid ? emailDef.validation.messages.invalid : pick(lang, FORM_MESSAGES.emailInvalid)
      };
    }

    // Validate message
    const messageTrimmed = formData.message.trim();
    if (!messageTrimmed) {
      const errKey = messageDef?.id || "message";
      clientErrors[errKey] = {
        message: lang === "en" && messageDef?.validation?.messages?.required ? messageDef.validation.messages.required : pick(lang, FORM_MESSAGES.messageRequired)
      };
    } else if (messageTrimmed.length < 10) {
      const errKey = messageDef?.id || "message";
      clientErrors[errKey] = {
        message: pick(lang, FORM_MESSAGES.messageMinLength)
      };
    }
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      return;
    }
    setSubmitting(true);
    const valuesPayload: Record<string, string> = {};
    if (fullNameDef?.id) valuesPayload[fullNameDef.id] = nameTrimmed;
    if (companyDef?.id) valuesPayload[companyDef.id] = companyTrimmed;
    if (emailDef?.id) valuesPayload[emailDef.id] = emailTrimmed;
    if (phoneDef?.id && formData.phone.trim()) valuesPayload[phoneDef.id] = formData.phone.trim();
    if (messageDef?.id) valuesPayload[messageDef.id] = messageTrimmed;
    await performSubmit(formDef, valuesPayload, honeypotVal, 0);
  };
  if (loadState === "loading") {
    return <div className="space-y-4 py-4" aria-busy="true">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-44" />
          <Skeleton className="h-32 w-full rounded-md" />
        </div>
        <Skeleton className="h-11 w-full rounded-md" />
      </div>;
  }
  if (loadState === "error" || !formDef) {
    return <div className="py-6 text-center space-y-4">
        <Alert variant="destructive" className="max-w-xl mx-auto text-left">
          <AlertCircle className="size-4" />
          <AlertTitle>{pick(lang, FORM_MESSAGES.loadErrorTitle)}</AlertTitle>
          <AlertDescription>
            {loadErrorMsg || pick(lang, FORM_MESSAGES.loadErrorDescription)}
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => fetchFormDefinition()} className="mt-2 gap-2">
          <RefreshCw className="size-4" />
          {pick(lang, FORM_MESSAGES.retryLoading)}
        </Button>
      </div>;
  }
  if (submittedSuccess) {
    return <div className="p-8 text-center rounded-lg bg-card border border-border flex flex-col items-center justify-center min-h-[420px]">
        <div className="size-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="mf-h3 font-semibold text-foreground font-default mb-2">
          {pick(lang, FORM_MESSAGES.submitSuccessTitle)}
        </h3>
        {confirmationHtml ? <div className="text-muted-foreground text-sm max-w-sm mb-6 leading-relaxed space-y-2 [&>p]:leading-relaxed" dangerouslySetInnerHTML={{
        __html: confirmationHtml
      }} /> : <p className="text-muted-foreground text-sm max-w-sm mb-6 leading-relaxed">
            {pick(lang, FORM_MESSAGES.submitSuccessDefaultHtml).replace(/<\/?p>/g, "")}
          </p>}
        <Button onClick={handleReset} variant="outline" size="sm">
          {pick(lang, FORM_MESSAGES.submitAnother)}
        </Button>
      </div>;
  }
  const fullNameDef = getFieldDef("full_name");
  const companyDef = getFieldDef("company");
  const emailDef = getFieldDef("email");
  const phoneDef = getFieldDef("phone");
  const messageDef = getFieldDef("message");
  const fullNameErr = fieldErrors[fullNameDef?.id || "full_name"]?.message;
  const companyErr = fieldErrors[companyDef?.id || "company"]?.message;
  const emailErr = fieldErrors[emailDef?.id || "email"]?.message;
  const phoneErr = fieldErrors[phoneDef?.id || "phone"]?.message;
  const messageErr = fieldErrors[messageDef?.id || "message"]?.message;
  const labelFullName = lang === "en" && fullNameDef?.label ? `${fullNameDef.label} *` : pick(lang, FORM_MESSAGES.labelFullName);
  const placeholderFullName = lang === "en" && fullNameDef?.placeholder ? fullNameDef.placeholder : pick(lang, FORM_MESSAGES.placeholderFullName);
  const labelCompany = lang === "en" && companyDef?.label ? `${companyDef.label} *` : pick(lang, FORM_MESSAGES.labelCompany);
  const placeholderCompany = lang === "en" && companyDef?.placeholder ? companyDef.placeholder : pick(lang, FORM_MESSAGES.placeholderCompany);
  const labelEmail = lang === "en" && emailDef?.label ? `${emailDef.label} *` : pick(lang, FORM_MESSAGES.labelEmail);
  const placeholderEmail = emailDef?.placeholder || "name@company.com";
  const labelPhone = lang === "en" && phoneDef?.label ? phoneDef.label : pick(lang, FORM_MESSAGES.labelPhone);
  const placeholderPhone = lang === "en" && phoneDef?.placeholder ? phoneDef.placeholder : pick(lang, FORM_MESSAGES.placeholderPhone);
  const labelMessage = lang === "en" && messageDef?.label ? `${messageDef.label} *` : pick(lang, FORM_MESSAGES.labelMessage);
  const placeholderMessage = lang === "en" && messageDef?.placeholder ? messageDef.placeholder : pick(lang, FORM_MESSAGES.placeholderMessage);
  const processingText = lang === "en" && formDef.submit?.processing_label ? formDef.submit.processing_label : pick(lang, FORM_MESSAGES.submittingText);
  const submitText = lang === "en" && formDef.submit?.label ? formDef.submit.label : pick(lang, FORM_MESSAGES.submitButtonText);
  return <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {/* Visually hidden honeypot decoy when enabled */}
      {formDef.spam?.honeypot?.field && <div style={{
      position: "absolute",
      left: "-9999px",
      opacity: 0
    }} aria-hidden="true">
          <label htmlFor={formDef.spam.honeypot.field}>Leave this empty</label>
          <input id={formDef.spam.honeypot.field} type="text" name={formDef.spam.honeypot.field} value={honeypotVal} onChange={e => setHoneypotVal(e.target.value)} tabIndex={-1} autoComplete="off" />
        </div>}

      {generalError && <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>{pick(lang, FORM_MESSAGES.noteTitle)}</AlertTitle>
          <AlertDescription>{generalError}</AlertDescription>
        </Alert>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Full Name */}
        <div className="space-y-2">
          <Label htmlFor="inquiry_full_name" className="text-foreground mf-caption font-semibold uppercase tracking-wider block">
            {labelFullName}
          </Label>
          <Input id="inquiry_full_name" placeholder={placeholderFullName} value={formData.full_name} onChange={e => handleInputChange("full_name", e.target.value)} disabled={submitting} aria-invalid={Boolean(fullNameErr)} className="bg-background text-foreground" />
          {fullNameErr && <p className="mf-caption text-destructive font-medium">{fullNameErr}</p>}
        </div>

        {/* Company Name */}
        <div className="space-y-2">
          <Label htmlFor="inquiry_company" className="text-foreground mf-caption font-semibold uppercase tracking-wider block">
            {labelCompany}
          </Label>
          <Input id="inquiry_company" placeholder={placeholderCompany} value={formData.company} onChange={e => handleInputChange("company", e.target.value)} disabled={submitting} aria-invalid={Boolean(companyErr)} className="bg-background text-foreground" />
          {companyErr && <p className="mf-caption text-destructive font-medium">{companyErr}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="inquiry_email" className="text-foreground mf-caption font-semibold uppercase tracking-wider block">
            {labelEmail}
          </Label>
          <Input id="inquiry_email" type="email" placeholder={placeholderEmail} value={formData.email} onChange={e => handleInputChange("email", e.target.value)} disabled={submitting} aria-invalid={Boolean(emailErr)} className="bg-background text-foreground" />
          {emailErr && <p className="mf-caption text-destructive font-medium">{emailErr}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="inquiry_phone" className="text-foreground mf-caption font-semibold uppercase tracking-wider block">
            {labelPhone}
          </Label>
          <Input id="inquiry_phone" type="tel" placeholder={placeholderPhone} value={formData.phone} onChange={e => handleInputChange("phone", e.target.value)} disabled={submitting} aria-invalid={Boolean(phoneErr)} className="bg-background text-foreground" />
          {phoneErr && <p className="mf-caption text-destructive font-medium">{phoneErr}</p>}
        </div>
      </div>

      {/* Message */}
      <div className="space-y-2">
        <Label htmlFor="inquiry_message" className="text-foreground mf-caption font-semibold uppercase tracking-wider block">
          {labelMessage}
        </Label>
        <Textarea id="inquiry_message" placeholder={placeholderMessage} rows={4} value={formData.message} onChange={e => handleInputChange("message", e.target.value)} disabled={submitting} aria-invalid={Boolean(messageErr)} className="min-h-32 bg-background text-foreground resize-y" />
        {messageErr && <p className="mf-caption text-destructive font-medium">{messageErr}</p>}
      </div>

      <Button type="submit" disabled={submitting} className="w-full font-medium" size="lg">
        {submitting ? <span className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            {processingText}
          </span> : <span className="flex items-center gap-2">
            <Send className="size-4" />
            {submitText}
          </span>}
      </Button>

      <p className="mf-caption text-muted-foreground text-center pt-2">
        {pick(lang, FORM_MESSAGES.privacyNotice)}
      </p>
    </form>;
}
const COPY = {
  badge: {
    en: "Industrial B2B Support",
    zh: "工业级 B2B 技术支持",
    es: "Soporte industrial B2B",
    de: "Industrieller B2B-Support",
    fr: "Support industriel B2B",
    pt: "Suporte industrial B2B"
  },
  h1: {
    en: "Let's discuss your requirements.",
    zh: "让我们探讨您的需求。",
    es: "Hablemos de sus requisitos.",
    de: "Lassen Sie uns Ihre Anforderungen besprechen.",
    fr: "Parlons de vos exigences.",
    pt: "Vamos analisar as suas necessidades."
  },
  lead: {
    en: "Whether you need technical clarification on stainless steel weave specifications, standard roll inventory, custom slit coils, or precision fabricated filter elements, our engineering team is here to assist.",
    zh: "无论您需要关于不锈钢编织规格的技术咨询、标准卷料现货库存、定制分条窄带，还是精密加工滤芯元件，我们的工程团队随时为您提供专业支持。",
    es: "Tanto si necesita aclaraciones técnicas sobre especificaciones de tejido en acero inoxidable, stock de rollos estándar, bobinas cortadas a medida o elementos filtrantes de precisión, nuestro equipo técnico está a su disposición.",
    de: "Ob Sie technische Klärung zu Edelstahldrahtgewebe-Spezifikationen, Standard-Rollenlager, individuell geschlitzten Bändern oder präzise gefertigten Filterelementen benötigen – unser Ingenieurteam unterstützt Sie gerne.",
    fr: "Que vous ayez besoin de précisions techniques sur le tissage d'acier inoxydable, de rouleaux standard en stock, de bandes refendues sur mesure ou d'éléments filtrants de précision, notre équipe technique est là pour vous assister.",
    pt: "Seja para esclarecimentos técnicos sobre especificações de tecelagem em aço inoxidável, estoque de rolos padrão, bobinas cortadas sob medida ou elementos filtrantes de precisão, nossa equipe de engenharia está à disposição."
  },
  quoteBoxTitle: {
    en: "Need standard quote calculations?",
    zh: "需要标准规格快速报价？",
    es: "¿Necesita cálculos de presupuesto estándar?",
    de: "Benötigen Sie Standard-Angebotsberechnungen?",
    fr: "Besoin d'un devis standard ?",
    pt: "Precisa de cotações para especificações padrão?"
  },
  quoteBoxText: {
    en: "For standard volume pricing and immediate spec evaluation, you can also use our formal quote tool.",
    zh: "如需大批量标准规格阶梯报价与即时参数评估，亦可使用我们的在线报价工具。",
    es: "Para precios por volumen estándar y evaluación inmediata de especificaciones, también puede utilizar nuestra herramienta de presupuesto formal.",
    de: "Für Staffelpreise bei Standardmengen und sofortige Bewertung Ihrer Spezifikation können Sie auch unser formelles Angebotstool nutzen.",
    fr: "Pour des tarifs dégressifs sur volumes standard et une évaluation immédiate de vos spécifications, vous pouvez également utiliser notre outil de devis formel.",
    pt: "Para preços por volume padrão e avaliação imediata de especificações, você também pode usar nossa ferramenta formal de orçamento."
  },
  quoteBoxLink: {
    en: "Go to Quote Request System →",
    zh: "进入在线报价系统 →",
    es: "Ir al sistema de solicitud de presupuesto →",
    de: "Zum Angebotsanfragesystem →",
    fr: "Accéder au système de demande de devis →",
    pt: "Acessar o sistema de solicitação de orçamento →"
  },
  emailTitle: {
    en: "Direct Technical Email",
    zh: "技术咨询与工程邮箱",
    es: "Correo técnico directo",
    de: "Direkte technische E-Mail",
    fr: "E-mail technique direct",
    pt: "E-mail técnico direto"
  },
  emailSub: {
    en: "Inquiries, spec sheets, and drawings",
    zh: "接收询盘、技术图纸及规格表",
    es: "Consultas, fichas técnicas y planos",
    de: "Anfragen, Datenblätter und Zeichnungen",
    fr: "Demandes, fiches techniques et plans",
    pt: "Consultas, fichas de especificação e desenhos"
  },
  phoneTitle: {
    en: "Commercial Desk",
    zh: "商务与销售热线",
    es: "Atención comercial",
    de: "Vertrieb & Kundenservice",
    fr: "Service commercial",
    pt: "Atendimento comercial"
  },
  phoneSub: {
    en: "Commercial desk & technical support",
    zh: "商务咨询与技术支持",
    es: "Mesa comercial y soporte técnico",
    de: "Vertriebs- und technische Unterstützung",
    fr: "Bureau commercial et support technique",
    pt: "Mesa comercial e suporte técnico"
  },
  trustPoint1: {
    en: "Application engineers response",
    zh: "工程技术团队直接响应",
    es: "Respuesta directa de ingenieros de aplicaciones",
    de: "Direkte Antwort durch Anwendungsingenieure",
    fr: "Réponse directe des ingénieurs d'application",
    pt: "Resposta direta de engenheiros de aplicação"
  },
  trustPoint2: {
    en: "Custom specs & drawings supported",
    zh: "支持定制图纸与非标规格",
    es: "Soporte para planos y especificaciones a medida",
    de: "Unterstützung für Sondermaße und Zeichnungen",
    fr: "Prise en charge des plans et spécifications sur mesure",
    pt: "Suporte a desenhos e especificações sob medida"
  },
  formCardTitle: {
    en: "General Inquiry Form",
    zh: "通用技术咨询与询盘表单",
    es: "Formulario de consulta general",
    de: "Allgemeines Anfrageformular",
    fr: "Formulaire de demande générale",
    pt: "Formulário de consulta geral"
  },
  formCardDesc: {
    en: "Complete the fields below and our technical sales department will respond shortly.",
    zh: "请填写以下信息，我们的工业技术销售部门将尽快与您取得联系。",
    es: "Complete los siguientes campos y nuestro departamento de ventas técnicas se pondrá en contacto en breve.",
    de: "Füllen Sie die folgenden Felder aus und unser technischer Vertrieb wird sich in Kürze mit Ihnen in Verbindung setzen.",
    fr: "Remplissez les champs ci-dessous et notre service technico-commercial vous répondra rapidement.",
    pt: "Preencha os campos abaixo e nosso departamento de vendas técnicas entrará em contato em breve."
  },
  facilityCardTitle: {
    en: "Technical Support & Production Facility",
    zh: "技术工程支持与生产基地",
    es: "Soporte técnico e instalaciones de producción",
    de: "Technischer Support & Fertigungsstätte",
    fr: "Support technique et site de fabrication",
    pt: "Suporte técnico e unidade de produção"
  },
  facilityCardAlt: {
    en: "Stainless steel woven wire mesh weaving loom and precision inspection setup in industrial facility",
    zh: "工业车间内的不锈钢编织丝网织网机与精密检测台",
    es: "Telar de malla de alambre tejida de acero inoxidable y estación de inspección de precisión en planta industrial",
    de: "Edelstahl-Drahtgewebe-Webmaschine und Präzisionsprüfstation im Industriebetrieb",
    fr: "Métier à tisser la toile métallique en acier inoxydable et poste de contrôle de précision en atelier industriel",
    pt: "Tear de malha de arame tecida em aço inoxidável e bancada de inspeção de precisão em instalação industrial"
  },
  facilityCardDesc: {
    en: "Technical drawings, custom slit coil requirements, and OEM specifications are handled directly by our engineering desk.",
    zh: "技术图纸、定制分条窄带要求及 OEM 规格均由我们的工程技术中心直接审核处理。",
    es: "Los planos técnicos, requisitos de bobinas cortadas a medida y especificaciones OEM son gestionados directamente por nuestra oficina de ingeniería.",
    de: "Technische Zeichnungen, geschlitzte Schmalbandanforderungen und OEM-Spezifikationen werden direkt von unserem Ingenieurbüro bearbeitet.",
    fr: "Les plans techniques, spécifications de refendage sur mesure et exigences OEM sont traités directement par notre bureau d'études.",
    pt: "Desenhos técnicos, requisitos de corte em fitas e especificações OEM são analisados diretamente por nossa equipe de engenharia."
  }
};
export default function Inquiry() {
  const {
    lang
  } = useLanguage();
  return <section id="generalinquirycontact" data-nav="light" className="bg-muted/40 text-foreground mf-hero border-b border-border relative overflow-hidden">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
            <Reveal>
              <div>
                <Badge variant="outline" className="bg-muted text-foreground mb-4 mf-caption font-semibold px-3 py-1 uppercase tracking-wider border border-border">
                  {pick(lang, COPY.badge)}
                </Badge>
                <h1 className="mf-h1 font-default text-foreground">
                  {pick(lang, COPY.h1)}
                </h1>
                <p className="text-muted-foreground mf-lead mt-4 mb-4 font-default">
                  {pick(lang, COPY.lead)}
                </p>

                <div className="p-4 rounded-lg bg-card border border-border mb-4">
                  <div className="flex items-start gap-3">
                    <FileCheck2 className="size-5 text-primary shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-foreground">
                        {pick(lang, COPY.quoteBoxTitle)}
                      </p>
                      <p className="text-muted-foreground mt-0.5">
                        {pick(lang, COPY.quoteBoxText)}
                      </p>
                      <div className="mt-2">
                        <Link to="/RequestAQuotePage" className="text-primary font-medium mf-caption hover:underline inline-flex items-center gap-1">
                          {pick(lang, COPY.quoteBoxLink)}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={60}>
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                
                <div className="p-4">
                  <h2 className="text-sm font-semibold text-foreground font-default">
                    {pick(lang, COPY.facilityCardTitle)}
                  </h2>
                  <p className="mf-caption text-muted-foreground mt-1 leading-relaxed">
                    {pick(lang, COPY.facilityCardDesc)}
                  </p>
                </div>
              </div>
            </Reveal>

            <Separator className="my-1" />

            <div className="space-y-4">
              <Reveal delay={120}>
                <div data-index={0} className="flex items-start gap-4 p-3 rounded-lg bg-card border border-border">
                  <div className="p-2.5 rounded-lg bg-background border border-border text-foreground shrink-0">
                    <Mail className="size-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground font-default">
                      {pick(lang, COPY.emailTitle)}
                    </h3>
                    <p className="mf-caption text-muted-foreground mb-1">
                      {pick(lang, COPY.emailSub)}
                    </p>
                    <a href="mailto:sales@mutualfilter.com" className="text-sm font-medium text-primary hover:underline">sales@mutualfilter.com</a>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={180}>
                <div data-index={1} className="flex items-start gap-4 p-3 rounded-lg bg-card border border-border">
                  <div className="p-2.5 rounded-lg bg-background border border-border text-foreground shrink-0">
                    <Phone className="size-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground font-default">
                      {pick(lang, COPY.phoneTitle)}
                    </h3>
                    <p className="mf-caption text-muted-foreground mb-1">
                      {pick(lang, COPY.phoneSub)}
                    </p>
                    <a href="tel:+8618033666533" className="text-sm font-medium text-primary hover:underline">(86) 180-3366-6533</a>
                  </div>
                </div>
              </Reveal>
            </div>

            <Reveal delay={240}>
              <div className="pt-2 flex flex-wrap items-center gap-6 mf-caption text-muted-foreground border-t border-border">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" />
                  {pick(lang, COPY.trustPoint1)}
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" />
                  {pick(lang, COPY.trustPoint2)}
                </span>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={100}>
              <Card className="border border-border bg-card text-card-foreground rounded-lg">
                <CardContent className="p-4 sm:p-6">
                  <div className="mb-4">
                    <h2 className="mf-h3 font-bold font-default text-card-foreground">
                      {pick(lang, COPY.formCardTitle)}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1 font-default">
                      {pick(lang, COPY.formCardDesc)}
                    </p>
                  </div>

                  <ContactInquiryForm lang={lang} />
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>
      </div>
    </section>;
}